import { HttpError } from '@littlebird/auth-client';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { type BaseModalFCProps } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Box, XStack, YStack } from '@/components/ui/layout/layout';
import { handleExternalUrlClick } from '@/utils/platform';

import {
  useChannelConnectionQuery,
  useChannelLinkCodeMutation,
  useDisconnectChannelMutation,
} from './msg-channels.query';

import type { MessagingChannelType } from './msg-channels.types';

export type ChannelConnectModalProps = BaseModalFCProps & {
  channelType: MessagingChannelType;
  channelName: string;
  channelIconSrc: string;
  openButtonLabel: string;
  currentIdentifier?: string | null;
};

export const ChannelConnectModal = ({
  channelType,
  channelName,
  channelIconSrc,
  openButtonLabel,
  open,
  onOpenChange,
  currentIdentifier,
}: ChannelConnectModalProps) => {
  const linkCode = useChannelLinkCodeMutation(channelType);
  const disconnectMutation = useDisconnectChannelMutation(channelType);
  const [codeExpired, setCodeExpired] = useState(false);

  const connectedHandle = currentIdentifier ?? null;
  const isConnected = Boolean(connectedHandle);
  const code = linkCode.data;

  // Poll while waiting: the user's message creates the binding server-side; stop once linked.
  const connectionQuery = useChannelConnectionQuery(channelType, {
    refetchInterval: open && !isConnected ? 3000 : false,
  });

  // Mint a fresh code each time the modal opens unconnected.
  useEffect(() => {
    if (open && !isConnected) {
      linkCode.reset();
      linkCode.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A minted code is short-lived. Flip to an expired state once its window lapses.
  useEffect(() => {
    if (!open || !code) return;
    setCodeExpired(false);
    const timer = setTimeout(
      () => setCodeExpired(true),
      code.expires_in_seconds * 1000
    );
    return () => clearTimeout(timer);
  }, [open, code]);

  // The poll found a binding the user just created — close.
  useEffect(() => {
    if (open && !currentIdentifier && connectionQuery.data?.identifier) {
      toast.success(`${channelName} connected`);
      onOpenChange(false);
    }
  }, [
    open,
    currentIdentifier,
    connectionQuery.data?.identifier,
    onOpenChange,
    channelName,
  ]);

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync();
      toast.success(`${channelName} channel disconnected`);
      onOpenChange(false);
    } catch (err) {
      console.error('[msg-channels] disconnect failed', err);
      toast.error(
        `Failed to disconnect ${channelName} channel. Please try again.`
      );
    }
  };

  const isChannelUnavailable =
    linkCode.error instanceof HttpError && linkCode.error.status === 503;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        data-testid={`channel-connect-modal-${channelType}`}
        className="max-w-md gap-0 p-0 overflow-hidden"
      >
        <Dialog.Header
          className={isConnected ? 'px-6 pb-6 pt-6' : 'px-6 pb-4 pt-6'}
        >
          <Dialog.Title>
            {isConnected ? `Manage ${channelName}` : `Connect ${channelName}`}
          </Dialog.Title>
          <Dialog.Description
            className={
              isConnected ? 'sr-only' : 'mt-1 text-sm text-foreground-muted'
            }
          >
            {isConnected
              ? `Manage your ${channelName} connection.`
              : 'Link your account in a few seconds. No password needed.'}
          </Dialog.Description>
        </Dialog.Header>

        {!isConnected && (
          <YStack className="gap-6 px-6 py-4">
            {linkCode.isPending && (
              <YStack center className="h-64 gap-3 text-foreground-muted">
                <Icons.loader className="size-5 animate-spin" />
                <span className="text-sm">Creating a secure link...</span>
              </YStack>
            )}
            {linkCode.isError && (
              <YStack center className="h-64 gap-4 text-center">
                <Box className="grid size-10 place-items-center rounded-full bg-danger-3 text-danger-11">
                  <Icons.cancel className="size-5" />
                </Box>
                <YStack className="gap-1">
                  <p className="text-sm font-medium text-foreground">
                    {isChannelUnavailable
                      ? `${channelName} is unavailable`
                      : "Couldn't create a connection code"}
                  </p>
                  <p className="max-w-64 text-xs text-foreground-muted">
                    {isChannelUnavailable
                      ? `${channelName} isn't available right now. Try again later.`
                      : 'Try again in a moment.'}
                  </p>
                </YStack>
                {!isChannelUnavailable && (
                  <Button variant="outline" onClick={() => linkCode.mutate()}>
                    Try again
                  </Button>
                )}
              </YStack>
            )}
            {code && codeExpired && (
              <YStack
                data-testid="channel-code-expired"
                center
                className="h-64 gap-4 text-center"
              >
                <Box className="grid size-10 place-items-center rounded-full bg-danger-3 text-danger-11">
                  <Icons.cancel className="size-5" />
                </Box>
                <YStack className="gap-1">
                  <p className="text-sm font-medium text-foreground">
                    This code expired
                  </p>
                  <p className="max-w-64 text-xs text-foreground-muted">
                    Connection codes last a few minutes. Generate a fresh one to
                    connect.
                  </p>
                </YStack>
                <Button
                  data-testid="channel-generate-new-code-button"
                  variant="outline"
                  onClick={() => linkCode.mutate()}
                >
                  Generate new code
                </Button>
              </YStack>
            )}
            {code && !codeExpired && (
              <>
                <YStack center className="gap-4">
                  <Box className="rounded-3xl border border-border-strong bg-white p-2 shadow-sm">
                    <img
                      data-testid="channel-connect-qr"
                      src={`data:image/svg+xml,${encodeURIComponent(code.qr_svg)}`}
                      alt={`QR code that opens ${channelName} with the code filled in`}
                      className="size-44"
                    />
                  </Box>
                  <YStack center className="gap-1">
                    <p className="text-base font-medium text-foreground">
                      Scan to connect
                    </p>
                    <p className="text-sm text-foreground-muted">
                      Chat with Littlebird on {channelName}
                    </p>
                  </YStack>
                </YStack>

                <XStack className="w-full items-center gap-3">
                  <Box className="h-px flex-1 bg-border-strong" />
                  <span className="text-xs text-foreground-muted">
                    or continue on this device
                  </span>
                  <Box className="h-px flex-1 bg-border-strong" />
                </XStack>

                <Button
                  data-testid="channel-open-in-app-button"
                  variant="default"
                  size="lg"
                  className="w-full"
                  onClick={() => handleExternalUrlClick(code.deep_link)}
                >
                  {openButtonLabel}
                  <Icons.externalLink2 className="size-3.5" />
                </Button>
              </>
            )}
          </YStack>
        )}

        {isConnected && (
          <YStack className="px-6 pb-8 pt-2">
            <XStack className="w-full items-center gap-3">
              <img
                src={channelIconSrc}
                alt=""
                className="size-10 shrink-0 rounded-lg object-cover"
              />
              <YStack className="min-w-0 flex-1 gap-0.5">
                <p className="text-sm font-medium text-foreground">Connected</p>
                <p
                  data-testid="channel-connected-identifier"
                  className="truncate text-sm text-foreground-muted"
                >
                  {connectedHandle}
                </p>
              </YStack>
            </XStack>
          </YStack>
        )}

        {isConnected && (
          <Dialog.Footer className="gap-2 px-6 pb-6 sm:space-x-0">
            <Button
              data-testid="channel-disconnect-button"
              variant="secondary"
              type="button"
              onClick={handleDisconnect}
              isLoading={disconnectMutation.isPending}
              loadingText="Disconnecting"
              className="w-full sm:flex-1"
            >
              Disconnect
            </Button>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className="w-full sm:flex-1"
            >
              Done
            </Button>
          </Dialog.Footer>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};
